//#region src/v2/blocks/operation-block/helpers/persist-script-environment.ts
/** Normalize the store's environment scope, which may be an array or a plain record. */
var toEntries = (variables) => Array.isArray(variables) ? variables : Object.entries(variables).map(([key, value]) => ({
	key,
	value
}));
/**
* Resolve which environment variables a script changed and where to persist them.
*
* The active environment is a merge of workspace- and document-scoped variables:
* `getActiveEnvironment` concatenates them as `[...workspaceVariables, ...documentVariables]`.
* We use that ordering to map each changed variable back to the scope and index it came
* from, so updates mutate the correct collection in place instead of creating duplicates.
* New variables are added to whichever scope actually defines the active environment, so the
* upsert target exists (the mutator no-ops when the environment is missing from a collection).
*
* Variables present before the scripts ran but gone afterwards were removed via
* `pm.environment.unset()` and are emitted as deletes. Deletes splice the underlying array,
* so they are ordered last and by descending index — an earlier splice must not invalidate the
* index of a later one. Upserts run first because they replace in place without shifting.
*/
var getEnvironmentPersistenceActions = ({ environmentName, seededVariables, scriptVariables, mergedVariables, documentVariables, environmentExistsOnDocument }) => {
	const workspaceCount = Math.max(0, mergedVariables.length - documentVariables.length);
	const documentIndexByName = new Map(documentVariables.map((variable, index) => [variable.name, index]));
	const workspaceIndexByName = new Map(mergedVariables.slice(0, workspaceCount).map((variable, index) => [variable.name, index]));
	/**
	* Locate an existing variable by name, preferring the document scope on collisions since
	* document values win when the environment is merged for reads.
	*/
	const findExisting = (key) => {
		const documentIndex = documentIndexByName.get(key);
		if (documentIndex !== void 0) return {
			collectionType: "document",
			index: documentIndex
		};
		const workspaceIndex = workspaceIndexByName.get(key);
		if (workspaceIndex !== void 0) return {
			collectionType: "workspace",
			index: workspaceIndex
		};
	};
	const upserts = [];
	const deletes = [];
	const scriptKeys = /* @__PURE__ */ new Set();
	for (const { key, value } of toEntries(scriptVariables)) {
		scriptKeys.add(key);
		if (seededVariables[key] === value) continue;
		const existing = findExisting(key);
		if (existing) upserts.push({
			type: "upsert",
			environmentName,
			variable: {
				name: key,
				value
			},
			...existing
		});
		else upserts.push({
			type: "upsert",
			environmentName,
			variable: {
				name: key,
				value
			},
			collectionType: environmentExistsOnDocument ? "document" : "workspace"
		});
	}
	for (const key of Object.keys(seededVariables)) {
		if (scriptKeys.has(key)) continue;
		const documentIndex = documentIndexByName.get(key);
		if (documentIndex !== void 0) deletes.push({
			type: "delete",
			environmentName,
			index: documentIndex,
			collectionType: "document"
		});
		const workspaceIndex = workspaceIndexByName.get(key);
		if (workspaceIndex !== void 0) deletes.push({
			type: "delete",
			environmentName,
			index: workspaceIndex,
			collectionType: "workspace"
		});
	}
	deletes.sort((a, b) => b.index - a.index);
	return [...upserts, ...deletes];
};
//#endregion
export { getEnvironmentPersistenceActions };

//# sourceMappingURL=persist-script-environment.js.map